/**
 * Text for the bottom-centre prompt: what Interact would do, or what the ranger is busy with.
 * `key` is the label of whatever Interact is bound to. The expansion's own actions (10 §3) are worded
 * by the mission (`objective.useLabel`), with a plain default when it says nothing.
 */

import { useActionTicks } from '../sim/objectives';
import { interactOption } from '../sim/ranger';
import { BOMB, GEAR, RANGER } from '../sim/tuning';
import type { Captive } from '../sim/types';
import type { Sim } from '../sim/world';
import { dragEarnsUniform, uniformHint } from '../ui/hud/missionUniform';

/** How letting someone out is worded, by who he is. */
const FREE: Record<Captive['kind'], string> = {
  pow: 'FREE THE PRISONER', hostage: 'FREE THE PRISONER', officer: 'FREE THE PRISONER', scientist: 'FREE THE PRISONER',
  pilot: 'GET THE PILOT ON HIS FEET', defector: 'MEET THE CONTACT',
};

/** What pressing Interact would do right now, without the key. */
function offer(sim: Sim): string | null {
  const option = interactOption(sim);
  if (!option) return null;
  const label = sim.def.objective.useLabel;
  switch (option.kind) {
    case 'useObject': return label ?? 'USE IT';
    case 'lootBody': return label ?? 'SEARCH THE BODY';
    case 'deliver': return label ?? (option.place ? `PLACE THE ${option.item}` : `HAND OVER THE ${option.item}`);
    case 'meet': return label ?? FREE.defector;
    case 'drop': return 'DROP BODY';
    case 'drag': {
      if (option.enemy.state === 'wounded') return 'DRAG WOUNDED MAN';
      // The commonest way to meet the uniform rule is to haul a body to a doorway: say so.
      return dragEarnsUniform(sim.statics.def, sim.world) ? 'DRAG BODY - TO A DOOR FOR HIS UNIFORM' : 'DRAG BODY';
    }
    case 'loot': return 'TAKE AMMUNITION';
    case 'pod': return `OPEN POD ${'ABC'[option.index] ?? ''}`;
    case 'use': return option.what === 'radio' ? 'TAKE THE CODE BOOK' : 'KICK THE LEVER';
    case 'capture': return 'TAKE HIM PRISONER';
    case 'free': {
      // The originals never set a label, so they read as they always have.
      const who = sim.world.objective.captives.find((c) => c.id === option.captiveId)?.kind ?? 'pow';
      return who === 'pow' || who === 'hostage' ? FREE[who] : (label ?? FREE[who]);
    }
    case 'uniform': return 'TAKE HIS UNIFORM';
    case 'tags': return `RECOVER ${option.name.toUpperCase()}'S TAGS`;
    case 'salvage': return option.what === 'grenade' ? 'TAKE HIS GRENADE' : 'TAKE A MAGAZINE';
    case 'cut': return 'CUT THE WIRE';
    default: return null;
  }
}

export function promptFor(sim: Sim, key = 'E'): string | null {
  const r = sim.world.ranger;
  const mods = sim.world.mods;
  const secsLeft = (total: number): number => Math.ceil(((total - r.actionTicks) / 60) * 10) / 10;
  if (r.action === 'placeBomb') return `ARMING BOMB ${secsLeft(BOMB.placeTicks * mods.bombPlaceTime)}s - HOLD STILL`;
  if (r.action === 'firstAid') return `FIRST AID ${secsLeft(RANGER.firstAidTicks * mods.firstAidTime)}s - HOLD STILL`;
  const hazards = sim.world.hazards;
  if (hazards.wireTicks > 0) {
    const left = Math.ceil((hazards.wireTicks / 60) * 10) / 10;
    return mods.wireImmune ? 'SLIPPING THROUGH THE WIRE' : `CAUGHT ON THE WIRE - TEARING FREE ${left}s`;
  }
  if (hazards.wirePushTicks > 0) {
    return mods.wireImmune ? 'KEEP PUSHING TO SLIP THROUGH THE WIRE' : 'KEEP PUSHING TO FORCE THE WIRE - IT WILL COST A WOUND';
  }
  if (r.action === 'cutWire') return `CUTTING WIRE ${secsLeft(GEAR.cutTicks)}s - HOLD STILL`;
  if (r.action === 'useObject') {
    return `${sim.def.objective.useLabel ?? 'WORKING'} ${secsLeft(useActionTicks(sim.def))}s - HOLD STILL`;
  }
  if (r.action === 'board') return 'CLIMBING THE LADDER';
  const text = offer(sim);
  if (text !== null) return `[${key}] ${text}`;
  // Nothing to press: say what the uniform route is missing, if he is standing in half of it.
  return uniformHint(sim.statics.def, sim.world);
}
